import React from "react";
import { Link } from "react-router-dom";

/**
 * ANATRADEX Academy — Chartist Track
 * Cours complet dédié à l'analyse graphique / price action / lecture institutionnelle.
 * Progression pédagogique : Débutant (fondations) → Semi-pro (patterns, Fibonacci) → Pro (ICT, Elliott, synthèse).
 */
export const CHARTISTE_ARTICLES = [
  {
    slug: "chartisme-101-lecture-graphique",
    track: "chartistes",
    level: "Débutant",
    minutes: 6,
    order: 1,
    title: "Chartisme 101 : lire un graphique comme un pro",
    summary: "Types de graphiques, unités de temps, échelle logarithmique. Les fondations avant tout.",
    content: (
      <>
        <p>Avant de plonger dans les figures et indicateurs, comprenez ce que représente un graphique.</p>
        <h3>Les 3 types de graphiques</h3>
        <ul>
          <li><b>Ligne</b> : ne montre que le prix de clôture. Bon pour les tendances longues.</li>
          <li><b>Barres OHLC</b> : Open High Low Close. Trace 4 informations par barre.</li>
          <li><b>Bougies japonaises</b> : mêmes 4 informations que OHLC mais avec un « corps » coloré (vert = hausse, rouge = baisse). <b>Standard des chartistes modernes.</b></li>
        </ul>
        <h3>Les unités de temps (timeframes)</h3>
        <p>Chaque bougie représente une durée : M1 = 1 minute, M15 = 15 min, H1 = 1 heure, H4 = 4 heures, D1 = jour, W1 = semaine, MN = mois. <b>Règle d&apos;or : analysez d&apos;abord la grande TF (D1/H4) puis affinez en H1/M15.</b></p>
        <h3>Échelle logarithmique vs linéaire</h3>
        <p>Sur ANATRADEX, vous pouvez basculer entre les deux. <b>Log</b> = les mouvements en % apparaissent équivalents (utile sur crypto avec ×10 en un an). <b>Linéaire</b> = les mouvements en points sont équivalents (utile sur forex).</p>
        <h3>Prochaine étape</h3>
        <p>Une fois cette base acquise, plongez dans <Link to="/academy/bougies-japonaises-anatomie" className="text-accent-blue underline">l&apos;anatomie des bougies japonaises</Link>.</p>
      </>
    ),
  },
  {
    slug: "bougies-japonaises-anatomie",
    track: "chartistes",
    level: "Débutant",
    minutes: 7,
    order: 2,
    title: "Bougies japonaises : anatomie et 8 figures clés",
    summary: "Doji, Marteau, Étoile filante, Engulfing… Le vocabulaire visuel indispensable.",
    content: (
      <>
        <p>Chaque bougie raconte l&apos;histoire d&apos;une bataille entre acheteurs et vendeurs sur sa période. Apprenez à la lire.</p>
        <h3>Anatomie</h3>
        <p><b>Corps</b> = espace entre open et close. Grand corps = mouvement fort. Petit corps = indécision.<br />
        <b>Mèches</b> (upper/lower shadow) = extrêmes atteints puis rejetés. Longue mèche basse sur une bougie verte = achat institutionnel.</p>
        <h3>8 figures à retenir</h3>
        <ol>
          <li><b>Doji</b> : open ≈ close. Indécision. Signal de renversement potentiel.</li>
          <li><b>Marteau</b> : petit corps en haut, longue mèche basse. Rejet des vendeurs. Signal haussier après baisse.</li>
          <li><b>Étoile filante (Shooting Star)</b> : petit corps en bas, longue mèche haute. Rejet des acheteurs. Signal baissier après hausse.</li>
          <li><b>Marubozu</b> : grand corps, quasi pas de mèche. Contrôle total d&apos;un côté.</li>
          <li><b>Engulfing haussier</b> : grande bougie verte qui englobe la précédente rouge. Retournement fort.</li>
          <li><b>Engulfing baissier</b> : miroir inverse.</li>
          <li><b>Harami</b> : petite bougie contenue dans la précédente. Ralentissement du mouvement.</li>
          <li><b>Étoile du matin/soir</b> : 3 bougies signalant un retournement majeur.</li>
        </ol>
        <p><b>Attention</b> : ces figures ne fonctionnent qu&apos;en contexte (près d&apos;un support/résistance, dans un régime cohérent). Une bougie isolée n&apos;a pas de valeur prédictive statistique.</p>
      </>
    ),
  },
  {
    slug: "support-resistance-fondations",
    track: "chartistes",
    level: "Débutant",
    minutes: 6,
    order: 3,
    title: "Supports & résistances : trouver les niveaux clés",
    summary: "Comment identifier les zones de prix qui comptent vraiment (et éviter les faux niveaux).",
    content: (
      <>
        <p>Un support/résistance, c&apos;est un niveau où le prix a rebondi plusieurs fois. C&apos;est <b>le concept central</b> du chartisme.</p>
        <h3>Comment les identifier</h3>
        <ul>
          <li><b>Swing highs / swing lows</b> : pivots visibles à l&apos;œil nu sur la grande TF. Sur ANATRADEX, activez l&apos;indicateur <b>Swing High/Low</b> pour les voir automatiquement.</li>
          <li><b>Zones psychologiques</b> : nombres ronds (1.1000 sur EURUSD, 100k sur BTC).</li>
          <li><b>Anciens plus hauts / plus bas</b> : le prix a une mémoire.</li>
        </ul>
        <h3>Zones vs lignes</h3>
        <p><b>Erreur débutant</b> : tracer une ligne exacte. <b>Réalité</b> : c&apos;est une <em>zone</em> de quelques pips/points. Utilisez l&apos;outil Rectangle du menu Dessin de ANATRADEX pour délimiter la zone.</p>
        <h3>Rôle inversé</h3>
        <p>Un support cassé devient une résistance. Une résistance cassée devient un support. C&apos;est ce que ANATRADEX appelle un <b>Breaker Block</b> (activez-le dans la section Structure du menu Indicateurs).</p>
        <h3>Setup type</h3>
        <p>1. Identifier une zone de support en H4. 2. Attendre que le prix revienne sur cette zone. 3. Chercher une bougie de rejet (marteau, engulfing). 4. Entrée avec stop sous la zone, cible sur la résistance suivante.</p>
      </>
    ),
  },
  {
    slug: "tendances-canaux",
    track: "chartistes",
    level: "Débutant",
    minutes: 6,
    order: 4,
    title: "Tendances et canaux : trader avec le flux",
    summary: "Higher Highs, Lower Lows, canaux ascendants/descendants. « The trend is your friend. »",
    content: (
      <>
        <p>Une tendance haussière = <b>higher highs + higher lows</b>. Une tendance baissière = <b>lower highs + lower lows</b>. Consolidation = pas de HH/LL clairs.</p>
        <h3>Tracer une ligne de tendance</h3>
        <ol>
          <li>Identifier 2 pivots dans le même sens (2 lows dans une tendance haussière).</li>
          <li>Tracer la droite entre eux (outil « Ligne » du menu Dessin de ANATRADEX).</li>
          <li>Étendre à droite. Un 3ᵉ touché confirme la validité.</li>
        </ol>
        <h3>Canaux</h3>
        <p>Un canal = 2 lignes parallèles. Le prix rebondit entre les deux. Utilisez l&apos;outil <b>Canal parallèle</b> (menu Dessin) pour le tracer en 2 clics.</p>
        <h3>Cassure de tendance</h3>
        <p>Quand le prix casse la ligne de tendance <b>avec volume</b>, la tendance est probablement terminée. C&apos;est ce que ANATRADEX détecte automatiquement sous le nom <b>CISD</b> (Change in State of Delivery) — activez-le dans la section Structure.</p>
        <h3>Règle d&apos;or</h3>
        <p><em>Ne trade jamais contre la tendance de la TF supérieure.</em> Si D1 est haussier, n&apos;essaie pas de shorter en M15.</p>
      </>
    ),
  },
  {
    slug: "figures-chartistes-classiques",
    track: "chartistes",
    level: "Semi-pro",
    minutes: 8,
    order: 5,
    title: "Figures chartistes classiques : H&S, double top, triangles",
    summary: "10 figures reconnaissables + probabilités historiques + comment les valider.",
    content: (
      <>
        <p>Ces figures existent depuis Charles Dow (1900). Elles fonctionnent parce qu&apos;elles reflètent des <b>comportements psychologiques de masse</b>.</p>
        <h3>Figures de retournement</h3>
        <ul>
          <li><b>Tête-Épaules (H&S)</b> : 3 sommets, celui du milieu plus haut. Casse de la « ligne de cou » = signal baissier. Objectif = hauteur de la tête reportée en bas.</li>
          <li><b>H&S inversé</b> : même figure retournée, signal haussier.</li>
          <li><b>Double top / bottom</b> : 2 sommets/creux à peu près au même niveau. Casse de la vallée intermédiaire = signal.</li>
          <li><b>Triple top / bottom</b> : version plus rare mais plus fiable.</li>
        </ul>
        <h3>Figures de continuation</h3>
        <ul>
          <li><b>Triangle ascendant</b> : résistance horizontale + supports croissants. Cassure haussière probable.</li>
          <li><b>Triangle descendant</b> : miroir inverse, cassure baissière probable.</li>
          <li><b>Triangle symétrique</b> : sommets décroissants + creux croissants. Neutre — cassure dans le sens de la tendance précédente.</li>
          <li><b>Drapeau (flag)</b> : petit canal contre la tendance suivant un mouvement fort. Continuation.</li>
          <li><b>Fanion (pennant)</b> : triangle serré suivant un mouvement fort. Continuation.</li>
          <li><b>Rectangle</b> : consolidation horizontale. Cassure = signal.</li>
        </ul>
        <h3>Comment valider</h3>
        <p>1. La figure doit être <b>visible sur au moins 20 bougies</b>. 2. Le volume doit décroître dans la formation et exploser à la cassure. 3. Confirmer avec un indicateur de momentum (MACD/RSI). 4. Objectif = hauteur de la figure reportée depuis le point de cassure.</p>
        <p><b>Piège</b> : les faux breakouts sont fréquents (surtout en range). Attendez une bougie de confirmation, pas la mèche.</p>
      </>
    ),
  },
  {
    slug: "fibonacci-en-pratique",
    track: "chartistes",
    level: "Semi-pro",
    minutes: 8,
    order: 6,
    title: "Fibonacci : retracements, extensions, éventails",
    summary: "Comment tracer et interpréter les niveaux 38.2, 50, 61.8, 78.6 % (et les extensions).",
    content: (
      <>
        <p>Les niveaux de Fibonacci ne sont pas magiques — ils fonctionnent parce que <b>beaucoup de traders les surveillent</b>, créant une réalité auto-réalisatrice. Utilisez-les comme des zones probables de réaction.</p>
        <h3>Retracements — niveaux clés</h3>
        <ul>
          <li><b>23.6 %</b> : retracement peu profond, souvent ignoré.</li>
          <li><b>38.2 %</b> : premier vrai support/résistance dans une tendance forte.</li>
          <li><b>50 %</b> : mathématiquement pas Fibonacci mais psychologiquement dominant.</li>
          <li><b>61.8 %</b> : le « nombre d&apos;or » — retracement le plus surveillé. <b>Zone à ne pas casser dans une tendance saine.</b></li>
          <li><b>78.6 %</b> : dernier retracement avant invalidation de la tendance.</li>
        </ul>
        <h3>Comment tracer sur ANATRADEX</h3>
        <p>Menu <b>Dessin</b> → <b>Fib Retracement</b> → cliquer sur le swing low, puis sur le swing high (ou l&apos;inverse en tendance baissière). Ou utiliser le bouton <b>« Auto-détecter Fibonacci »</b> qui détecte automatiquement le dernier swing majeur des 60 dernières bougies.</p>
        <h3>Extensions</h3>
        <p>Pour projeter des <b>objectifs de prix</b> après un retracement, utilisez les extensions : 127.2, 161.8, 261.8. La 161.8 = objectif classique d&apos;une vague d&apos;impulsion.</p>
        <h3>Éventail de Fibonacci</h3>
        <p>Trace des lignes obliques depuis un pivot. Utile pour identifier des supports/résistances dynamiques dans une tendance.</p>
        <h3>Confluences</h3>
        <p>Le vrai pouvoir : combiner Fibonacci avec un support/résistance horizontal existant. <b>Confluence = fiabilité ×3.</b></p>
      </>
    ),
  },
  {
    slug: "vagues-elliott",
    track: "chartistes",
    level: "Pro",
    minutes: 10,
    order: 7,
    title: "Théorie des vagues d'Elliott : compter le marché",
    summary: "Impulsion 5 vagues, correction ABC, extensions. Le framework fractal des marchés.",
    content: (
      <>
        <p>Ralph Nelson Elliott (1930s) a observé que les marchés se déplacent en <b>cycles récurrents de 5 vagues d&apos;impulsion suivies de 3 vagues de correction</b>. C&apos;est fractal : chaque vague contient elle-même des sous-vagues.</p>
        <h3>La structure 5-3</h3>
        <p><b>Impulsion (5 vagues)</b> :<br />
        - Vague 1 : premier mouvement dans le nouveau sens.<br />
        - Vague 2 : correction (ne dépasse pas le début de la vague 1).<br />
        - Vague 3 : la plus longue et forte (ne peut pas être la plus courte).<br />
        - Vague 4 : correction (ne recouvre pas la vague 1).<br />
        - Vague 5 : dernière impulsion (souvent avec divergence MACD/RSI).<br />
        <b>Correction (3 vagues ABC)</b> : contre-mouvement en 3 parties.</p>
        <h3>3 règles absolues</h3>
        <ol>
          <li>La vague 2 ne dépasse jamais le début de la vague 1.</li>
          <li>La vague 3 n&apos;est jamais la plus courte des vagues 1, 3, 5.</li>
          <li>La vague 4 ne pénètre pas dans le territoire de la vague 1.</li>
        </ol>
        <p>Si une de ces règles est violée, votre décompte est faux — recomptez.</p>
        <h3>Comment tracer sur ANATRADEX</h3>
        <p>Menu <b>Dessin</b> → <b>Elliott 5 vagues</b> ou <b>Elliott 3 vagues</b>. Cliquez sur les 5 pivots (ou 3 pour ABC). L&apos;outil dessine automatiquement les libellés.</p>
        <h3>Fibonacci + Elliott</h3>
        <p><b>Combinaison ultime</b> :<br />
        - Vague 2 retrace typiquement 50-61.8 % de la vague 1.<br />
        - Vague 3 fait souvent 161.8 % de la vague 1.<br />
        - Vague 4 retrace 38.2 % de la vague 3.<br />
        - Vague 5 = extension 61.8 % de (vague 1+3).</p>
        <p><b>Attention</b> : Elliott est <em>subjectif</em>. Deux analystes trouveront deux décomptes valables. Utilisez-le comme un cadre d&apos;interprétation, jamais comme une règle mécanique.</p>
      </>
    ),
  },
  {
    slug: "order-blocks-fvg-lecture-institutionnelle",
    track: "chartistes",
    level: "Pro",
    minutes: 8,
    order: 8,
    title: "Order Blocks & Fair Value Gaps : lecture institutionnelle",
    summary: "Repérer les zones où les fonds achètent et vendent. Le trading « Smart Money ».",
    content: (
      <>
        <p>Les fonds institutionnels laissent des <b>traces graphiques</b> quand ils exécutent de gros ordres. Apprenez à les lire.</p>
        <h3>Order Block (OB)</h3>
        <p>C&apos;est la <b>dernière bougie opposée à l&apos;impulsion</b>. Ex : dans une phase baissière, la dernière bougie verte avant une chute massive est un OB baissier. Les institutions ont probablement placé leurs ordres de vente pendant cette bougie.</p>
        <p><b>Comment l&apos;utiliser</b> : quand le prix revient sur cet OB, c&apos;est une zone de forte réaction attendue. Activez <b>OB</b> dans la section Structure du menu Indicateurs — ANATRADEX les détecte automatiquement.</p>
        <h3>Fair Value Gap (FVG)</h3>
        <p>Un déséquilibre de 3 bougies : le high de la bougie 1 est <em>inférieur</em> au low de la bougie 3 (gap haussier). Le prix « saute » une zone, laissant une inefficience qu&apos;il remplit souvent plus tard.</p>
        <p><b>Comment l&apos;utiliser</b> : cible pour take-profit ou zone d&apos;entrée à contre-tendance courte. Activez <b>FVG</b> dans la section Structure.</p>
        <h3>Breaker Block (BB)</h3>
        <p>Un OB qui a été cassé puis retesté depuis le côté opposé (role reversal). L&apos;ancien support devient résistance et inversement. Signal encore plus fort qu&apos;un OB simple.</p>
        <h3>Setup complet Smart Money</h3>
        <ol>
          <li>Identifier le biais directionnel D1 (tendance).</li>
          <li>Identifier un OB ou BB non-comblé sur H4.</li>
          <li>Descendre sur H1/M15 et attendre que le prix revienne sur la zone.</li>
          <li>Chercher une confirmation (CISD, engulfing, marteau).</li>
          <li>Entrée avec stop derrière l&apos;OB, cible sur la prochaine FVG ou swing.</li>
        </ol>
      </>
    ),
  },
  {
    slug: "kill-zones-sessions",
    track: "chartistes",
    level: "Pro",
    minutes: 6,
    order: 9,
    title: "Kill-zones et sessions : quand trader ?",
    summary: "Asia, London, New York AM/PM. Les fenêtres à haute volatilité où l'edge se cache.",
    content: (
      <>
        <p>Tout le monde ne trade pas au même moment. Les grandes banques et fonds concentrent leur activité sur des <b>fenêtres précises</b>. Ces fenêtres sont les « kill-zones ».</p>
        <h3>Les 4 kill-zones institutionnelles (heure UTC)</h3>
        <ul>
          <li><b>Asia</b> : 23:00 → 04:00 UTC. Faible volatilité, définition des ranges.</li>
          <li><b>London</b> : 07:00 → 10:00 UTC. Ouverture européenne, forte volatilité.</li>
          <li><b>New York AM</b> : 12:00 → 15:00 UTC. Recouvrement London-NY, la plus explosive.</li>
          <li><b>New York PM</b> : 17:30 → 20:00 UTC. Réversion, prises de profit.</li>
        </ul>
        <h3>Comment les utiliser</h3>
        <p>Activez le bouton <b>Kill-zones</b> dans la toolbar ANATRADEX. Vous verrez des rectangles colorés (violet Asia, bleu London, vert NY AM, orange NY PM) sur votre graphique.</p>
        <h3>Stratégies classiques</h3>
        <ul>
          <li><b>Asia range breakout</b> : identifier le high/low de la kill-zone Asia. Trader la cassure en London.</li>
          <li><b>Liquidity sweep NY AM</b> : le prix va souvent chercher les stops (au-dessus du HOD ou en-dessous du LOD) pendant la NY AM, puis renverse.</li>
          <li><b>Judas swing</b> : mouvement trompeur dans une direction en early London, suivi d&apos;un reversal violent.</li>
        </ul>
        <p><b>Règle</b> : évitez de trader en dehors des kill-zones (sauf swing D1). Le bruit prend le dessus, l&apos;edge disparaît.</p>
      </>
    ),
  },
  {
    slug: "synthese-chartisme-quant",
    track: "chartistes",
    level: "Pro",
    minutes: 7,
    order: 10,
    title: "Synthèse : combiner chartisme et quant",
    summary: "L'approche ANATRADEX — le meilleur des deux mondes.",
    content: (
      <>
        <p>Chartisme pur = subjectif, sensible aux biais psychologiques. Quant pur = froid, ignore le contexte. <b>La combinaison bat les deux.</b></p>
        <h3>Le pipeline ANATRADEX recommandé</h3>
        <ol>
          <li><b>Contexte D1/H4</b> (chartisme) : tendance, régime, kill-zone active ?</li>
          <li><b>Zone d&apos;intérêt</b> (chartisme + auto-détection) : OB, FVG, support/résistance, Fibonacci 61.8 %.</li>
          <li><b>Signal statistique</b> (quant) : régime detection, probabilité, Deflated Sharpe.</li>
          <li><b>Confirmation timing</b> (chartisme M15/M5) : bougie de rejet, CISD, engulfing.</li>
          <li><b>Gestion du risque</b> (quant) : stop derrière la structure, taille de position selon Kelly ou % du compte.</li>
        </ol>
        <h3>Ce que chaque discipline apporte</h3>
        <p><b>Chartisme</b> = <em>identifier les niveaux clés</em>. C&apos;est le « où ». L&apos;analyse graphique est meilleure que l&apos;algo pour repérer les zones psychologiques importantes.</p>
        <p><b>Quant</b> = <em>quantifier la probabilité</em>. C&apos;est le « avec quelle confiance ». L&apos;algo est meilleur pour agréger 20 indicateurs simultanément et sortir un score objectif.</p>
        <h3>Setup type ANATRADEX (top-down)</h3>
        <p>Ouvrez la Dashboard, mettez XAUUSD en H1. Activez <b>Kill-zones + OB + FVG + BOLL + RSI</b>. Ouvrez un split H4 à côté. Le layout parfait = D1 pour le biais + H4 pour la zone + H1 pour l&apos;entrée + M15 pour la confirmation.</p>
        <p><b>Discipline</b> : ne prenez un trade que si <b>au moins 3 confluences</b> convergent (ex : OB H4 + Fibonacci 61.8 + Kill-zone London + Signal ANATRADEX haussier). En dessous = pas d&apos;edge.</p>
      </>
    ),
  },
];
